<script setup>
    import { date_options_2026 as date_options, skill_level_options, seating_options, availability_options } from '~~/shared/utilities/schedule';

    const { $bus, $clientPosthog } = useNuxtApp();

    const props = defineProps({
        objects: Number,
        teal: {
            type: Boolean,
            required: false,
            default: false,
        },
        advanced: {
            type: Boolean,
            required: false,
            default: false,
        },
        hideAdvanced: {
            type: Boolean,
            required: false,
            default: false,
        },
        startTime: {
            type: String,
            required: false,
            default: '',
        },
        endTime: {
            type: String,
            required: false,
            default: '',
        },
        year: {
            type: Number,
            required: false,
            default: null,
        },
    });

    const dates = ref(date_options);
    const skill_levels = ref(skill_level_options);
    const all_seating_options = ref(seating_options);

    const open = defineModel('open');
    // Special controls for calendar view
    const time_range = defineModel('time_range');
    const seating = defineModel('seating', 'all');
    const skill_level = defineModel('skill_level', 'all');
    const day = defineModel('day', '2025-09-03');
    const availability = defineModel('availability', { default: 'all' });

    const tags = defineModel('tags', { default: [] });

    // Fetch tag data to look up names from IDs
    const { data: tagData } = await useAsyncData(`all-tags-${props.year || 'default'}`, () => $fetch('/api/v1/session-tags', { params: props.year ? { year: props.year } : {} }));

    const getTagNames = (tagIds) => {
        if (!tagData.value || !tagIds?.length) return [];
        const names = [];
        for (const [groupName, groupTags] of Object.entries(tagData.value)) {
            for (const tag of groupTags) {
                if (tagIds.includes(tag.id)) {
                    names.push(tag.name);
                }
            }
        }
        return names;
    };

    const applyTags = () => {
        $clientPosthog?.capture('agenda_filter_applied', {
            selected_tags: getTagNames(tags.value),
            selected_tags_count: tags.value?.length || 0,
            results_count: props.objects,
            is_advanced_mode: props.advanced,
            day: props.advanced ? day.value : null,
            skill_level: props.advanced ? skill_level.value : null,
            seating: props.advanced ? seating.value : null,
        });
        open.value = false;
    };
    const clearTags = () => {
        tags.value = [];
    };
    const overlayClass = props.teal ? 'filter-overlay-teal' : 'filter-overlay';

    const drawerStyles = {
        overlay: 'bg-[var(--purple)]/80 backdrop-blur-xs',
    };

    watch(open, (val) => {
        if (val) $bus.emit('drawer-open');
        else $bus.emit('drawer-close');
        const hs = document.getElementById('hubspot-messages-iframe-container');
        if (hs) hs.style.display = val ? 'none' : '';
    });
</script>

<template>
    <UDrawer
        v-model:open="open"
        id="session-tags-drawer"
        direction="right"
        :dismissable="true"
        title="Filter"
        :handle="false"
        :handleOnly="true"
        :ui="drawerStyles"
        class="session-tags-drawer font-hubspot-sans">
        <template #header>
            <div class="advanced-controls" v-if="advanced">
                <div class="header">
                    <h3>Session Filters</h3>
                    <UButton color="primary" variant="outline" icon="i-lucide-x" class="rounded-full!" @click="open = false" />
                </div>
                <USelect v-model="availability" :items="availability_options" class="w-full select font-hubspot-serif" variant="outline" :ui="{ content: 'z-[2147483648] ring-0 font-hubspot-serif text-white', item: 'font-hubspot-serif text-white py-3' }" />
                <USelect v-if="advanced && !hideAdvanced" v-model="day" :items="dates" class="w-full select" variant="outline" :ui="{ content: 'z-[2147483648]' }" />
                <USelect v-if="advanced && !hideAdvanced" v-model="skill_level" :items="skill_levels" class="w-full select" variant="outline" :ui="{ content: 'z-[2147483648]' }" />
                <USelect v-if="advanced && !hideAdvanced" v-model="seating" :items="all_seating_options" class="w-full select" variant="outline" :ui="{ content: 'z-[2147483648]' }" />
                <div class="time-range" v-if="advanced && !hideAdvanced">
                    <USlider v-model="time_range" :min-steps-between-thumbs="3" :step="1" class="w-full thumbs" variant="md" :ui="{ track: 'bg-(--dark-pink)' }" />
                    <div class="ranges">
                        <span class="label" v-html="startTime"></span>
                        <span class="label" v-html="endTime"></span>
                    </div>
                </div>
            </div>
            <div class="header">
                <div class="headline">
                    <h3>
                        Filter by Topic <span class="count" v-if="tags.size">{{ tags.length }}</span>
                    </h3>
                    <p>Select all that apply</p>
                </div>
                <UButton class="closebutton rounded-full!" color="primary" variant="outline" icon="i-lucide-x" aria-label="Close filters" @click="open = false" />
            </div>
        </template>
        <template #body>
            <div class="inner">
                <FragmentsFiltersSessionTags v-model:tags="tags" :year="year" />
            </div>
        </template>
        <template #footer>
            <div class="drawer-footer">
                <div class="buttons">
                    <button
                        tabindex="0"
                        class="cta flex items-center rounded-[50px] py-2.5 px-5 max-w-fit font-hubspot-serif text-[calc(17/var(--base-fs)*1rem)] leading-[1.4] tracking-[0.34px] transition-all duration-250 whitespace-nowrap bg-white text-(--magenta) border border-(--magenta) hover:bg-(--magenta) hover:text-white"
                        @click="applyTags"
                        :aria-label="`Close this panel and see ${objects} results`">
                        See Results&nbsp;<span v-if="objects">({{ objects }})</span>
                    </button>
                    <button
                        tabindex="0"
                        class="bg-transparent text-(--dark-pink) decoration-dotted underline underline-offset-3 decoration-1 p-3.75 font-hubspot-serif text-[calc(17/var(--base-fs)*1rem)] leading-[1.4] tracking-[0.34px] transition-all duration-250 hover:no-underline"
                        @click="clearTags"
                        aria-label="Unselect all selected tags">
                        Clear All
                    </button>
                </div>
            </div>
        </template>
    </UDrawer>
</template>

<style>
    .session-tags-drawer .advanced-controls {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        margin-bottom: 2.3125rem;
    }

    .session-tags-drawer .advanced-controls .header {
        margin-bottom: 0.875rem;
    }

    .session-tags-drawer .advanced-controls .time-range {
        display: flex;
        flex-direction: column;
        align-items: center;
        border-radius: var(--radius);
        border: 1px solid var(--magenta);
        font-size: 0.875rem;
        font-weight: 300;
        line-height: 1.5625rem;
        letter-spacing: 0.01875rem;
        gap: 1rem;
        padding: 1.5rem 1.125rem 0.65rem 1.125rem;
    }

    .session-tags-drawer .advanced-controls .time-range .ranges {
        display: flex;
        justify-content: space-between;
        width: 100%;
    }

    .session-tags-drawer .advanced-controls .time-range span.label {
        width: 3.5rem;
        text-align: center;
    }

    .session-tags-drawer .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .session-tags-drawer .header p {
        color: var(--magenta);
        font-size: 0.875rem;
        font-style: normal;
        font-weight: 300;
        line-height: 1.25rem;
        letter-spacing: 0.0175rem;
    }

    .session-tags-drawer .header h3 {
        font-size: calc(30 / var(--base-fs) * 1rem);
        font-weight: 400;
        line-height: 2.25rem;
        letter-spacing: 0;
        color: var(--magenta);
        font-family: var(--font-hubspot-serif);
        display: flex;
        align-items: center;
        margin-bottom: 0.04375rem;
    }

    .session-tags-drawer .header h3 span {
        font-size: calc(24 / var(--base-fs) * 1rem);
        color: var(--dark-pink);
        margin-left: 1rem;
    }

    /* close button hover handled by UButton color="primary" variant="outline" */

    .session-tags-drawer .drawer-footer .buttons {
        display: flex;
        justify-content: start;
        align-self: center;
        flex-wrap: nowrap;
        gap: 1.625rem;
    }

    @media (min-width: 1024px) {
        .session-tags-drawer .drawer-footer .buttons {
            gap: 1.625rem;
        }
    }
</style>
